import React from 'react';
import { Calendar, TrendingDown, Users, Clock, AlertCircle, CheckCircle } from 'lucide-react';

const StatistiquesAbsences = ({ absencesEtudiants, absencesEnseignants, activeTab }) => {
  // Calculer les statistiques
  const totalAbsences = activeTab === 'etudiants' 
    ? absencesEtudiants.filter(a => a.absence).length 
    : absencesEnseignants.filter(a => a.absence).length;

  const absencesAujourdhui = activeTab === 'etudiants' 
    ? absencesEtudiants.filter(a => {
        const today = new Date().toISOString().split('T')[0];
        return a.date === today && a.absence;
      }).length
    : absencesEnseignants.filter(a => {
        const today = new Date().toISOString().split('T')[0];
        return a.date === today && a.absence;
      }).length;

  const absencesCetteSemaine = activeTab === 'etudiants'
    ? absencesEtudiants.filter(a => {
        const absenceDate = new Date(a.date);
        const today = new Date();
        const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
        return absenceDate >= weekStart && a.absence;
      }).length
    : absencesEnseignants.filter(a => {
        const absenceDate = new Date(a.date);
        const today = new Date();
        const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
        return absenceDate >= weekStart && a.absence;
      }).length;

  const absencesCeMois = activeTab === 'etudiants'
    ? absencesEtudiants.filter(a => {
        const absenceDate = new Date(a.date);
        const today = new Date();
        return absenceDate.getMonth() === today.getMonth() && 
               absenceDate.getFullYear() === today.getFullYear() && 
               a.absence;
      }).length
    : absencesEnseignants.filter(a => {
        const absenceDate = new Date(a.date);
        const today = new Date();
        return absenceDate.getMonth() === today.getMonth() && 
               absenceDate.getFullYear() === today.getFullYear() && 
               a.absence;
      }).length;

  // Calculer les top absents
  const absencesParPersonne = {};
  const currentData = activeTab === 'etudiants' ? absencesEtudiants : absencesEnseignants;
  
  currentData.filter(a => a.absence).forEach(absence => {
    const nom = activeTab === 'etudiants' 
      ? `${absence.etudiant?.prenom} ${absence.etudiant?.nom}`
      : `${absence.enseignant?.prenom} ${absence.enseignant?.nom}`;
    absencesParPersonne[nom] = (absencesParPersonne[nom] || 0) + 1;
  });

  const topAbsents = Object.entries(absencesParPersonne)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3);

  const cardsData = [
    {
      title: "Total Absences",
      value: totalAbsences,
      icon: Calendar,
      color: "bg-gradient-to-br from-purple-500 to-purple-600",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600"
    },
    {
      title: "Aujourd'hui",
      value: absencesAujourdhui,
      icon: Clock,
      color: "bg-gradient-to-br from-indigo-500 to-indigo-600",
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600"
    },
    {
      title: "Cette Semaine",
      value: absencesCetteSemaine,
      icon: TrendingDown,
      color: "bg-gradient-to-br from-yellow-500 to-yellow-600",
      iconBg: "bg-yellow-100",
      iconColor: "text-yellow-600"
    },
    {
      title: "Ce Mois",
      value: absencesCeMois,
      icon: AlertCircle,
      color: "bg-gradient-to-br from-pink-500 to-pink-600",
      iconBg: "bg-pink-100",
      iconColor: "text-pink-600"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Cartes de statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cardsData.map((card, index) => (
          <div key={index} className="relative group">
            <div className={`${card.color} rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium mb-1">{card.title}</p>
                  <p className="text-3xl font-bold">{card.value}</p>
                </div>
                <div className={`${card.iconBg} rounded-full p-3`}>
                  <card.icon className={`h-6 w-6 ${card.iconColor}`} />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <div className="flex-1 bg-white/20 rounded-full h-2">
                  <div 
                    className="bg-white/80 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((card.value / Math.max(totalAbsences, 1)) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Section des top absents */}
      {topAbsents.length > 0 && (
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-800 flex items-center">
              <Users className="h-5 w-5 mr-2 text-[#202654]" />
              Top Absents
            </h3>
            <div className="bg-[#202654] text-white px-3 py-1 rounded-full text-sm font-medium">
              {activeTab === 'etudiants' ? 'Étudiants' : 'Enseignants'}
            </div>
          </div>
          
          <div className="space-y-4">
            {topAbsents.map(([nom, count], index) => (
              <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      index === 0 ? 'bg-red-500' : 
                      index === 1 ? 'bg-orange-500' : 
                      'bg-yellow-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{nom}</p>
                      <p className="text-sm text-gray-600">{count} absence{count > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-sm font-medium">
                      {count}
                    </div>
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${(count / Math.max(...Object.values(absencesParPersonne))) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Message d'encouragement */}
      {totalAbsences === 0 && (
        <div className="text-center py-12">
          <div className="bg-green-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Parfait ! Aucune absence enregistrée
          </h3>
          <p className="text-gray-600">
            Excellent taux de présence ! 🏆
          </p>
        </div>
      )}
    </div>
  );
};

export default StatistiquesAbsences; 